import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Map, Sparkles, Save, Send, Loader2, ListChecks, Plus, CheckCircle2, Eye, EyeOff, Undo2,
} from "lucide-react";

const linesToSteps = (txt) => txt.split("\n").map((l) => l.trim()).filter(Boolean).map((t) => ({ title: t }));
const stepsToLines = (arr) => (arr || []).map((s) => s.title || s).join("\n");
const linesToArr = (txt) => txt.split("\n").map((l) => l.trim()).filter(Boolean);

export const FamilyPlanPanel = ({ leadId }) => {
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ situation_summary: "", main_concern: "", next_steps: "", documents_needed: "" });
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [addingItem, setAddingItem] = useState(false);

  const loadItems = useCallback(() => api.get(`/checklist-items?lead_id=${leadId}`).then(({ data }) => setItems(data.items)).catch(() => {}), [leadId]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/roadmaps?lead_id=${leadId}`);
      const r = (data.items || [])[0] || null;
      setRoadmap(r);
      if (r) setForm({ situation_summary: r.situation_summary || "", main_concern: r.main_concern || "", next_steps: stepsToLines(r.next_steps), documents_needed: (r.documents_needed || []).join("\n") });
    } catch (e) { /* out of scope */ }
    finally { setLoading(false); }
  }, [leadId]);
  useEffect(() => { load(); loadItems(); }, [load, loadItems]);

  const createRoadmap = async () => {
    setBusy(true);
    try { const { data } = await api.post("/roadmaps", { lead_id: leadId }); setRoadmap(data.roadmap); toast.success("Draft roadmap created."); }
    catch (e) { toast.error(errMsg(e, "Could not create roadmap.")); } finally { setBusy(false); }
  };
  const aiDraft = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/roadmaps/${roadmap.id}/ai-draft`); const r = data.roadmap; setRoadmap(r); setForm({ situation_summary: r.situation_summary || "", main_concern: r.main_concern || "", next_steps: stepsToLines(r.next_steps), documents_needed: (r.documents_needed || []).join("\n") }); toast.success(data.ai_used ? "AI draft ready — review before publishing." : "AI returned no changes."); }
    catch (e) { toast.error(errMsg(e, "AI draft failed.")); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/roadmaps/${roadmap.id}`, { situation_summary: form.situation_summary, main_concern: form.main_concern, next_steps: linesToSteps(form.next_steps), documents_needed: linesToArr(form.documents_needed) }); setRoadmap(data.roadmap); toast.success("Saved."); }
    catch (e) { toast.error(errMsg(e, "Save failed.")); } finally { setBusy(false); }
  };
  const togglePublish = async () => {
    setBusy(true);
    const action = roadmap.status === "published" ? "unpublish" : "publish";
    try { const { data } = await api.post(`/roadmaps/${roadmap.id}/${action}`); if (data.roadmap) setRoadmap(data.roadmap); else setRoadmap({ ...roadmap, status: "draft" }); toast.success(action === "publish" ? "Published — the family can now see it." : "Unpublished."); }
    catch (e) { toast.error(errMsg(e, "Failed.")); } finally { setBusy(false); }
  };
  const addItem = async () => {
    if (!newItem.trim()) return; setAddingItem(true);
    try { await api.post("/checklist-items", { lead_id: leadId, title: newItem.trim(), visibility: "client", status: "approved", client_can_complete: true }); setNewItem(""); toast.success("Added to the family's checklist."); loadItems(); }
    catch (e) { toast.error(errMsg(e, "Could not add item.")); } finally { setAddingItem(false); }
  };
  const approveItem = async (id) => { try { await api.post(`/checklist-items/${id}/approve`); loadItems(); toast.success("Item approved."); } catch (e) { toast.error(errMsg(e, "Failed.")); } };

  if (loading) return null;

  return (
    <Card className="mt-4 p-4 border-[#E6EDF2]" data-testid="lead-family-plan-panel">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><Map size={13} /> Family transition roadmap</div>

      {!roadmap ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[13px] text-[#4B5563]">No roadmap yet. Create a draft, then optionally pre-fill it from an approved meeting summary.</div>
          <Button size="sm" onClick={createRoadmap} disabled={busy} className="bg-[#1C4358] hover:bg-[#163545] text-white gap-1.5" data-testid="roadmap-create-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create draft
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${roadmap.status === "published" ? "bg-[#2F7D5A]/12 text-[#2F7D5A]" : "bg-[#B7791F]/12 text-[#8a5d13]"}`} data-testid="roadmap-status">{roadmap.status === "published" ? "Published (client-visible)" : "Draft (internal only)"}</span>
            <Button size="sm" variant="outline" onClick={aiDraft} disabled={busy} className="border-[#D6E0E7] text-[#1C4358] gap-1.5" data-testid="roadmap-ai-draft-btn"><Sparkles size={14} /> AI draft from summary</Button>
          </div>
          <div>
            <Label className="text-[13px] text-[#4B5563]">Situation summary (family-facing)</Label>
            <Textarea value={form.situation_summary} onChange={(e) => setForm({ ...form, situation_summary: e.target.value })} rows={3} className="mt-1 text-[14px]" data-testid="roadmap-situation" />
          </div>
          <div>
            <Label className="text-[13px] text-[#4B5563]">Main concern</Label>
            <Input value={form.main_concern} onChange={(e) => setForm({ ...form, main_concern: e.target.value })} className="mt-1 h-10 text-[14px]" data-testid="roadmap-main-concern" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[13px] text-[#4B5563]">Next steps (one per line)</Label>
              <Textarea value={form.next_steps} onChange={(e) => setForm({ ...form, next_steps: e.target.value })} rows={4} className="mt-1 text-[14px]" data-testid="roadmap-next-steps" />
            </div>
            <div>
              <Label className="text-[13px] text-[#4B5563]">Documents needed (one per line)</Label>
              <Textarea value={form.documents_needed} onChange={(e) => setForm({ ...form, documents_needed: e.target.value })} rows={4} className="mt-1 text-[14px]" data-testid="roadmap-docs-needed" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} variant="outline" className="border-[#D6E0E7] text-[#1C4358] gap-1.5" data-testid="roadmap-save-btn"><Save size={14} /> Save</Button>
            <Button size="sm" onClick={togglePublish} disabled={busy} className={`${roadmap.status === "published" ? "bg-[#B7791F] hover:bg-[#9a6518]" : "bg-[#2F7D5A] hover:bg-[#276848]"} text-white gap-1.5`} data-testid="roadmap-publish-btn">
              {roadmap.status === "published" ? <><Undo2 size={14} /> Unpublish</> : <><Send size={14} /> Publish to family</>}
            </Button>
          </div>
        </div>
      )}

      {/* Family checklist quick-manage */}
      <div className="mt-5 pt-4 border-t border-[#EEF2F5]">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2 flex items-center gap-1.5"><ListChecks size={13} /> Family checklist ({items.length})</div>
        <div className="flex gap-2 mb-2">
          <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder="Add a client-visible step…" className="h-9 text-[14px]" data-testid="checklist-add-input" />
          <Button size="sm" onClick={addItem} disabled={addingItem} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1" data-testid="checklist-add-btn">{addingItem ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}</Button>
        </div>
        <div className="space-y-1.5 max-h-52 overflow-y-auto">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-2 text-[13px] rounded-lg border border-[#EEF2F5] px-2.5 py-1.5" data-testid={`staff-checklist-${c.id}`}>
              {c.status === "completed" ? <CheckCircle2 size={15} className="text-[#2F7D5A] shrink-0" /> : <span className="h-2 w-2 rounded-full bg-[#D6E0E7] shrink-0" />}
              <span className="flex-1 min-w-0 truncate text-[#252221]">{c.title}</span>
              {c.visibility === "client" ? <Eye size={13} className="text-[#2F7D5A]" title="Client-visible" /> : <EyeOff size={13} className="text-[#9AA5B1]" title="Internal" />}
              <span className="text-[11px] text-[#9AA5B1] capitalize">{c.status}</span>
              {c.status === "draft" && <button onClick={() => approveItem(c.id)} className="text-[11px] text-[#1C4358] hover:underline" data-testid={`checklist-approve-${c.id}`}>approve</button>}
            </div>
          ))}
          {items.length === 0 && <div className="text-[12px] text-[#9AA5B1]">No checklist items yet.</div>}
        </div>
      </div>
    </Card>
  );
};
